import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Flexbox,
  Heading,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  shootToast,
} from "codekit";

import {
  useCreateHistoryMutation,
  useLazyGetUsersChangeByNameQuery,
} from "../../../libs/redux/slices/AdminxxxSlice/adminxxxApiSlice";
import {
  clearCargoPreview,
  getCargoPreview,
  getEntrarcuAbierto,
  getInfoUser,
  getIsAdmin,
  getIsOtherUser,
  setChangeUser,
  setEntrarcuAbierto,
} from "../../../libs/redux/slices/authSlice/authSlice";
import { apiSlice } from "../../../libs/redux/slices/apiSlice";
import { Avisoxxx } from "../../Tablerox";

/**
 * "Entrar como usuario" (Diego 2026-09-14): complemento de "Ver como cargo".
 * Un administrador entra al sistema con la sesión real de otro usuario (token,
 * menú, datos y clientes de ese usuario), con historial en CIMS0077 vía
 * Parmpoxx/Adminxxx. Mientras dure, una franja recuerda a quién se suplanta y
 * ofrece "Volver a mi perfil" (el mismo retorno del menú del usuario). Si
 * estaba activo "Ver como cargo", se apaga antes de entrar.
 * @author Diego Serrano <diego.serrano@repremundo.com.co>
 * @since 2026-09-14
 * @param {function} handleClickHome Vuelve al Inicio (limpia módulo y proceso)
 * @param {function} onVolver        Retorno al perfil propio (returnUser de la página principal)
 */
const Entrarcub = ({ handleClickHome, onVolver }) => {
  const dispatch = useDispatch();
  const isAdmin = useSelector(getIsAdmin);
  const isOtherUser = useSelector(getIsOtherUser);
  const infoUser = useSelector(getInfoUser);
  const cargoPreview = useSelector(getCargoPreview);
  const isOpen = useSelector(getEntrarcuAbierto);
  const [usuario, setUsuario] = useState("");
  const [usuarioLabel, setUsuarioLabel] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscarUsuarios] = useLazyGetUsersChangeByNameQuery();
  const [crearIngreso, { isLoading }] = useCreateHistoryMutation();

  if (!isAdmin && !isOtherUser) {
    return null;
  }
  const cerrar = () => dispatch(setEntrarcuAbierto(false));

  //Todo el API vuelve a consultarse con la sesión nueva
  const reiniciar = () => {
    window.sessionStorage.removeItem("mipuestoAterrizado");
    dispatch(apiSlice.util.resetApiState());
    handleClickHome();
  };

  const handleBuscar = async (value, cb) => {
    await buscarUsuarios({ fields: "USRIDXXX,USRNOMXX", filters: { REGESTXX: "ACTIVO" }, query: value })
      .unwrap()
      .then((resp) =>
        setResultados(resp.data.map((row) => ({ value: row.USRIDXXX, label: `${row.USRIDXXX} - ${row.USRNOMXX}` }))),
      )
      .catch(() => setResultados([]));
    cb();
  };

  const handleEntrar = async () => {
    if (cargoPreview) {
      dispatch(clearCargoPreview());
    }
    await crearIngreso({ USRID2XX: usuario })
      .unwrap()
      .then((userData) => {
        dispatch(setChangeUser({ userData }));
        setUsuario("");
        setUsuarioLabel("");
        reiniciar();
      })
      .catch((error) =>
        shootToast({
          variant: "error",
          text: error?.data?.errors?.message ?? "No se pudo entrar como ese usuario",
          duration: 4000,
          autoHide: true,
        }),
      );
  };

  return (
    <>
      {isOtherUser && (
        <Box mb={3}>
          <Avisoxxx
            tonoxxx="NARANJA"
            kicker="Modo de mantenimiento · sesión de otro usuario"
            valor={infoUser?.USRIDXXX ?? ""}
            titulo={`Entraste como ${infoUser?.USRNOMXX ?? ""}`}
            texto="Ves su menú, su Mi Puesto, sus clientes y sus datos. Todo lo que guardes queda registrado a nombre de ese usuario: vuelve a tu perfil al terminar."
            acciones={[{ clave: "volver", texto: "Volver a mi perfil", primaria: true, onClick: onVolver }]}
          />
        </Box>
      )}
      {isOpen && (
        <Modal size="md" isOpen={isOpen}>
          <ModalHeader>
            <Flexbox justifyContent="between" alignItems="center">
              <Heading variant="heading-5" color="primary-700">
                Entrar como otro usuario
              </Heading>
              <Button variant="secondary" icon="close" onlyIcon size="m" onClick={cerrar} />
            </Flexbox>
          </ModalHeader>
          <ModalBody size="sm">
            <Label>Usuario</Label>
            <Autocomplete
              placeholder="Escribe cédula o nombre (mínimo 2 caracteres)"
              textLoading="Buscando..."
              textNoResults="No se encontraron usuarios"
              minLengthToSearch={2}
              searchResults={resultados}
              selected={usuarioLabel}
              onChangeSearch={handleBuscar}
              onItemClick={(context) => {
                setUsuarioLabel(context.item.label);
                setUsuario(String(context.item.value));
              }}
              onResetSelected={() => {
                setUsuarioLabel("");
                setUsuario("");
              }}
            />
            <Box mt={3}>
              <Label color="neutral-500">
                Entrarás con la sesión real de ese usuario: su menú, su Mi Puesto, sus clientes y sus permisos. El
                ingreso queda en el historial y todo lo que guardes se registra a su nombre.
              </Label>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Flexbox justifyContent="end" alignItems="center" gap={4}>
              <Button
                type="button"
                variant="primary-bold"
                size="2l"
                minWidth={160}
                isLoading={isLoading}
                disabled={!usuario}
                onClick={handleEntrar}
              >
                Entrar como este usuario
              </Button>
            </Flexbox>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default Entrarcub;
